import React, { useEffect, useMemo, useState } from 'react';
import { withRouter } from "react-router-dom";
import DataTable from 'react-data-table-component';
import moment from 'moment';
import Api from '../Services/Api';
import Alert from '../Services/Alert';
import { getDateFormat } from '../Services/Custom';

const CURRENCY = process.env.REACT_APP_CURRENCY_SYMBOL || '₹';
const money = (v) => `${CURRENCY}${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * One page for every ledger report (feature F). The report is chosen by the :type route
 * param and its definition lives on the server, so adding a report needs no change here.
 *
 * Follows the TransactionList template: server-side paging, the filter panel re-fetches by
 * flipping isReset, and the summary comes back on page 1 only.
 */
export const LedgerReport = (props) => {
    const type = props.match.params.type;

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);

    const [searchFromDate, setSearchFromDate] = useState('');
    const [searchToDate, setSearchToDate] = useState('');
    const [search, setSearch] = useState('');
    const [isReset, setIsReset] = useState(false);

    const dfltSummary = { count: 0, total_amount: 0, breakdown: [] };
    const [summary, setSummary] = useState(dfltSummary);
    const [title, setTitle] = useState('Report');
    const [description, setDescription] = useState('');

    const columns = useMemo(() => [
        { name: 'Date', selector: row => moment(row.createdAt).format('DD-MM-YYYY hh:mm A'), width: '170px' },
        { name: 'Mobile', selector: row => row.mobile_no || '-', width: '130px' },
        { name: 'Details', selector: row => row.details || '-', wrap: true },
        { name: 'Wallet', selector: row => row.wallet_type_label || '-', width: '130px' },
        { name: 'Type', selector: row => row.tnx_type_label || '-', width: '150px' },
        { name: 'Reference', selector: row => row.tnx || row.order_id || '-', wrap: true },
        { name: 'Amount', selector: row => money(row.amount), right: true, width: '130px' },
    ], []);

    function fetchReport(page, size = perPage) {
        setLoading(true);
        const sendd = { type, page, size, searchFromDate, searchToDate, search };
        Api.post('report/ledger', sendd).then(resp => {
            const r = resp.data;
            if (r.status == 1) {
                setData(r.data.docs);
                setTotalRows(r.total);
                setTitle(r.reportLabel || 'Report');
                setDescription(r.reportDescription || '');
                if (page === 1) setSummary(r.summaryData || dfltSummary);
            } else {
                Alert.error(r.message);
                setData([]); setTotalRows(0); setSummary(dfltSummary);
            }
            setLoading(false);
        }).catch(err => { setLoading(false); Alert.error(err.message); });
    }

    function handlePageChange(page) { fetchReport(page); }
    function handlePerRowsChange(newPerPage, page) { setPerPage(newPerPage); fetchReport(page, newPerPage); }

    const handleFilter = () => setIsReset(!isReset);
    const handleResetFilter = () => {
        setSearchFromDate(''); setSearchToDate(''); setSearch('');
        setIsReset(!isReset);
    };

    /* Re-fetch when the filter is applied AND when the route changes, so navigating between
       reports in the menu reloads rather than showing the previous report's rows. */
    useEffect(() => { fetchReport(1); }, [isReset, type]);

    return (
        <section className="main-content marketadd_main_sec">
            <div className="page-content">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="card">
                                <div className="card-body">
                                    <h4 className="card-title mb-1">{title}</h4>
                                    {description ? <p className="text-muted">{description}</p> : null}

                                    <div className="border border-primary p-3 mb-4">
                                        <div className="row">
                                            <div className="col-lg-3">
                                                <label className="form-label">From</label>
                                                <input type="date" className="form-control" max={getDateFormat()}
                                                    value={searchFromDate} onChange={e => setSearchFromDate(e.target.value)} />
                                            </div>
                                            <div className="col-lg-3">
                                                <label className="form-label">To</label>
                                                <input type="date" className="form-control" max={getDateFormat()} min={searchFromDate}
                                                    value={searchToDate} onChange={e => setSearchToDate(e.target.value)} />
                                            </div>
                                            <div className="col-lg-3">
                                                <label className="form-label">Search (mobile no.)</label>
                                                <input type="text" className="form-control" placeholder="e.g. 98765..."
                                                    value={search} onChange={e => setSearch(e.target.value)} />
                                            </div>
                                            <div className="col-lg-3 d-flex align-items-end">
                                                <button className="btn btn-primary me-2" onClick={handleFilter}>Filter</button>
                                                <button className="btn btn-light" onClick={handleResetFilter}>Reset</button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="table-responsive mb-4">
                                        <table className="table align-middle table-nowrap mb-0">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>Summary</th>
                                                    <th className="text-end">Transactions</th>
                                                    <th className="text-end">Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {summary.breakdown && summary.breakdown.length > 0
                                                    ? summary.breakdown.map((b, k) => (
                                                        <tr key={k}>
                                                            <td><span className="badge bg-success">{b.label}</span></td>
                                                            <td className="text-end">{b.count}</td>
                                                            <td className="text-end">{money(b.total_amount)}</td>
                                                        </tr>
                                                    ))
                                                    : null}
                                            </tbody>
                                            <tfoot className="table-light">
                                                <tr>
                                                    <th>Total</th>
                                                    <th className="text-end">{summary.count}</th>
                                                    <th className="text-end">{money(summary.total_amount)}</th>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>

                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        progressPending={loading}
                                        pagination
                                        paginationServer
                                        paginationTotalRows={totalRows}
                                        onChangeRowsPerPage={handlePerRowsChange}
                                        onChangePage={handlePageChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default withRouter(LedgerReport);
